export const getMessageQueryKey = (receiver, companyId, search = "") => {
  const companySegment = companyId ?? "no-company";
  if (!receiver) return ["messages", companySegment, "unknown", search];
  return ["messages", companySegment, receiver.type, receiver.id ?? receiver.uid ?? "unknown", search];
};

const belongsToCompany = (recordCompanyId, companyId) => {
  if (recordCompanyId == null || companyId == null) return true;
  return Number(recordCompanyId) === Number(companyId);
};

const matchesEntity = (entity, identity) => {
  if (!entity || !identity) return false;
  if (identity.uid != null && entity.uid != null && String(entity.uid) === String(identity.uid)) return true;
  if (identity.id != null && entity.id != null && Number(entity.id) === Number(identity.id)) return true;
  return false;
};

export const messageBelongsToConversation = (messageRecord, receiver, { workforces, self, companyId } = {}) => {
  if (!messageRecord || !receiver) return false;
  if (!belongsToCompany(messageRecord.companyId, companyId)) return false;

  const { sender, receiver: msgReceiver } = messageRecord;
  const peerUid = receiver.uid;
  const peerId = receiver.id;

  const matchesPeerEntity = (entity) => matchesEntity(entity, { uid: peerUid, id: peerId });
  const matchesSelfEntity = (entity) => matchesEntity(entity, self);

  if (
    (matchesPeerEntity(sender) && matchesSelfEntity(msgReceiver)) ||
    (matchesPeerEntity(msgReceiver) && matchesSelfEntity(sender))
  ) {
    return true;
  }

  if (receiver.type === "company" && peerId != null) {
    const workforceUid = workforces?.find((w) => Number(w.companyId) === Number(peerId))?.uid;
    if (!workforceUid) return false;

    const involvesMyWorkforce = (entity) =>
      entity?.type === "workforce" && String(entity.uid) === String(workforceUid);
    const involvesCompany = (entity) =>
      entity?.type === "company" && Number(entity.id) === Number(peerId);

    if (involvesMyWorkforce(sender) && involvesCompany(msgReceiver)) return true;
    if (involvesCompany(sender) && involvesMyWorkforce(msgReceiver)) return true;
    if (sender?.type === "user" && involvesMyWorkforce(msgReceiver)) return true;
    if (msgReceiver?.type === "user" && involvesMyWorkforce(sender)) return true;
  }

  if (receiver.type === "workforce" && peerUid != null) {
    const involvesPeerWorkforce = (entity) =>
      entity?.type === "workforce" && String(entity.uid) === String(peerUid);

    if (involvesPeerWorkforce(sender) && msgReceiver?.type === "company") return true;
    if (involvesPeerWorkforce(msgReceiver) && sender?.type === "company") return true;
  }

  return false;
};

/**
 * Same peer-matching semantics as messageBelongsToConversation, but for a "messageRead"
 * socket payload whose `reader` is an array of source-entity identifiers instead of a
 * single message's sender/receiver pair.
 */
export const readerBelongsToConversation = (readerSources, receiver, { workforces, companyId, readCompanyId } = {}) => {
  if (!Array.isArray(readerSources) || !readerSources.length || !receiver) return false;
  if (!belongsToCompany(readCompanyId, companyId)) return false;

  const peerUid = receiver.uid;
  const peerId = receiver.id;

  const matchesPeerEntity = (entity) => matchesEntity(entity, { uid: peerUid, id: peerId });

  if (readerSources.some(matchesPeerEntity)) return true;

  if (receiver.type === "company" && peerId != null) {
    const workforceUid = workforces?.find((w) => Number(w.companyId) === Number(peerId))?.uid;
    if (workforceUid && readerSources.some((e) => e?.type === "workforce" && String(e.uid) === String(workforceUid))) {
      return true;
    }
  }

  if (receiver.type === "workforce" && peerUid != null) {
    if (readerSources.some((e) => e?.type === "workforce" && String(e.uid) === String(peerUid))) return true;
  }

  return false;
};
